import { useQuery } from "@tanstack/react-query";
import { contentApi, auditApi } from "../api";
import { Link } from "react-router-dom";
import { ArrowUpRight, Activity } from "lucide-react";

export default function Dashboard() {
  const { data } = useQuery({ queryKey: ["content"], queryFn: contentApi.get });
  const audit = useQuery({ queryKey: ["audit", 10, 0], queryFn: () => auditApi.list(10, 0) });

  const counts = {
    departments: data?.departments.length ?? 0,
    sections: data?.departments.reduce((acc, d) => acc + d.sections.length, 0) ?? 0,
    tiles:
      data?.departments.reduce(
        (acc, d) => acc + d.sections.reduce((a, s) => a + s.tiles.length, 0),
        0,
      ) ?? 0,
  };

  return (
    <div className="max-w-5xl mx-auto px-10 py-10">
      <header className="mb-8">
        <h1 className="display text-[36px] font-medium tracking-tight">
          Welcome <span className="italic">back</span>
        </h1>
        <p className="text-[13.5px] text-ink-500 mt-1.5">An overview of your CORE portal.</p>
      </header>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Stat label="Departments" value={counts.departments} />
        <Stat label="Sections" value={counts.sections} />
        <Stat label="Services" value={counts.tiles} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Link
          to="/admin/departments"
          className="surface p-5 hover:shadow-soft hover:-translate-y-0.5 transition-all group lg:col-span-1"
        >
          <div className="eyebrow">Quick action</div>
          <div className="mt-2.5 flex items-start justify-between gap-3">
            <div>
              <div className="display text-[18px] font-medium tracking-tight">Manage content</div>
              <p className="text-[12.5px] text-ink-500 mt-1 leading-[1.5]">
                Add and edit departments, sections, services.
              </p>
            </div>
            <ArrowUpRight className="size-4 text-ink-400 group-hover:text-ink-900 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0 mt-0.5" />
          </div>
        </Link>

        <div className="surface p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="eyebrow inline-flex items-center gap-1.5">
              <Activity className="size-3.5" /> Recent activity
            </div>
            <Link to="/admin/audit" className="text-[11.5px] text-ink-500 hover:text-ink-900">
              View all →
            </Link>
          </div>
          <ul className="space-y-1">
            {audit.data?.entries.slice(0, 8).map((e) => (
              <li key={e.id} className="flex items-center gap-2.5 py-1 text-[12px] text-ink-700">
                <span className="text-ink-400 font-mono shrink-0 w-32 text-[10.5px]">
                  {formatTime(e.ts)}
                </span>
                <span className="chip-mono">{e.action}</span>
                <span className="text-ink-500 truncate">{e.target}</span>
              </li>
            ))}
            {audit.data && audit.data.entries.length === 0 && (
              <li className="text-[12.5px] text-ink-400 py-4 text-center">No activity yet.</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="surface p-5">
      <div className="eyebrow">{label}</div>
      <div className="display text-[36px] font-medium tracking-tight text-ink-900 mt-1.5">
        {value}
      </div>
    </div>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
